import { makeAutoObservable, runInAction } from 'mobx';
import { ulid } from 'ulid';
import logger from '../helpers/logger';
import { store } from './store';
import enumHelper from '../helpers/enums';
import { v4 as guid } from 'uuid';
import passKeyHelper from '../helpers/passKey';
import sqlite from '../helpers/sqlite';
import { ApiCallServiceType } from '../types/ApiCall';
import { ReceiptType } from '../typegen/receipt-type';

export default class SessionStore {
	private _sessionId: string | undefined = undefined;
	private _legacyId: string | undefined = undefined;
	private _isSessionEnding: boolean = false;
	private _cleanupHandlers: Map<string, () => void | Promise<void>> = new Map();
	startedAt: number | null = null;
	lastActive: number | null = null;
	countdownSeconds: number | null = null;
	countdownHandle: ReturnType<typeof setTimeout> | null = null;
	countdownIntervalHandle: ReturnType<typeof setInterval> | null = null;
	monitoringIntervalHandle: ReturnType<typeof setInterval> | null = null;
	private _countdownThresholdMs: number | null = null;
	private _assumedSessionTimeLimitTotalMs: number | null = null;
	private _isTimerEnabled: boolean;
	private readonly _defaultCountdownThresholdRatio = 0.25; // 25% of total session time
	private _skipCountdownModal: boolean = false;
	private _timeoutNavigationCallback: (() => void) | null = null;

	constructor() {
		makeAutoObservable(this);
		this._isTimerEnabled = import.meta.env.VITE_DISABLE_SESSION_TIMER !== 'true';
	}

	get sessionId() {
		return this._sessionId;
	}

	get legacyId() {
		return this._legacyId;
	}

	get isTimerEnabled() {
		return this._isTimerEnabled;
	}

	get isKioAppAvailable() {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		return !!(window as any).KioApp;
	}

	get skipCountdownModal() {
		return this._skipCountdownModal;
	}

	setSkipCountdownModal = (skip: boolean) => {
		this._skipCountdownModal = skip;
	};

	setTimeoutNavigationCallback = (callback: (() => void) | null) => {
		this._timeoutNavigationCallback = callback;
	};

	registerCleanup = (key: string, handler: () => void | Promise<void>) => {
		this._cleanupHandlers.set(key, handler);
		logger.debug.log(`Cleanup handler registered: ${key}`);
	};

	unregisterCleanup = (key: string) => {
		this._cleanupHandlers.delete(key);
		logger.debug.log(`Cleanup handler unregistered: ${key}`);
	};

	/**
	 * Resets the Kioware inactivity timer by calling KioApp.BeginSession().
	 * This extends the current session and prevents timeout.
	 */
	beginKioWareSession = () => {
		if (this.isKioAppAvailable) KioApp.BeginSession();
	};

	startLoggingSessionData = async () => {
		if (this.sessionId || this._isSessionEnding) return;
		this.beginKioWareSession();

		this._sessionId = ulid();
		await sqlite.setSetting('currentSessionId', this.sessionId!);
		this._legacyId = guid();
		this.lastActive = this.startedAt = Date.now();
		logger.logGeneral.log({}, `Session started: ${this.sessionId}`);
		this.startCountdownTriggerMonitoring();
	};

	/**
	 * Clears the shared currentSessionId setting. Must only ever be called
	 * from the Main customer-facing window — calling it from any other
	 * window would let the cash-recovery sweep treat an in-progress
	 * payment in another session as abandoned. See PageRenderer's
	 * isConfigLoaded-gated effect, which is the only caller and only fires
	 * once loadConfig() has already awaited sqlite.init(), so — like its
	 * sibling writers — this method never needs to initialize sqlite itself.
	 */
	clearActiveSessionId = async () => {
		await sqlite.setSetting('currentSessionId', '');
	};

	touch = async () => {
		if (this.sessionId && this.isActive) {
			this.beginKioWareSession();
			this.lastActive = Date.now();

			const { value: currentSessionId, failed } = await sqlite.tryGetSetting('currentSessionId');
			if (!failed && currentSessionId !== this.sessionId) await sqlite.setSetting('currentSessionId', this.sessionId);
		}
	};

	get assumedSessionTimeLimitTotalMs(): number | null {
		if (this._assumedSessionTimeLimitTotalMs !== null && this._assumedSessionTimeLimitTotalMs !== -1) return this._assumedSessionTimeLimitTotalMs;

		// Calculate assumed session time limit from KioInactivityTimer samples
		if (this.isKioAppAvailable) {
			this.beginKioWareSession();
			const sample1 = KioInactivityTimer.GetTimeRemainingMs();
			this.beginKioWareSession();
			const sample2 = KioInactivityTimer.GetTimeRemainingMs();
			this.beginKioWareSession();
			const sample3 = KioInactivityTimer.GetTimeRemainingMs();
			// Subtract 3 seconds to end session before Kioware forcefully reloads
			this._assumedSessionTimeLimitTotalMs = Math.max(sample1, sample2, sample3);
		}

		return this._assumedSessionTimeLimitTotalMs;
	}

	startCountdownTriggerMonitoring = () => {
		if (!this.isTimerEnabled || !this.isKioAppAvailable) return;

		if (this.assumedSessionTimeLimitTotalMs === null) {
			logger.logGeneral.error({}, 'Cannot start countdown monitoring: unable to determine session time limit', null, {
				message: 'assumedSessionTimeLimitTotalMs is null',
			});
			return;
		}

		if (this.monitoringIntervalHandle !== null) clearInterval(this.monitoringIntervalHandle);

		// Determine countdown threshold if not already set
		if (this._countdownThresholdMs === null) {
			const customTimeout = store.configStore.getCustomTimerTimeout;
			if (customTimeout?.type === 'seconds') this._countdownThresholdMs = customTimeout.value * 1000;
			else if (customTimeout?.type === 'percent')
				this._countdownThresholdMs = (this.assumedSessionTimeLimitTotalMs * customTimeout.value) / 100;
			else this._countdownThresholdMs = this.assumedSessionTimeLimitTotalMs * this._defaultCountdownThresholdRatio; // Default to 25%

			// Correct threshold if it exceeds total session time
			if (customTimeout?.type === 'seconds' && this._countdownThresholdMs > this.assumedSessionTimeLimitTotalMs) {
				this._countdownThresholdMs = this.assumedSessionTimeLimitTotalMs * this._defaultCountdownThresholdRatio;
				logger.logGeneral.warn(
					{},
					`Custom session timeout warning time prompt exceeds total session time, defaulting to ${this._defaultCountdownThresholdRatio * 100}% threshold`,
					{
						customTimeoutSeconds: customTimeout.value,
						assumedSessionTimeLimitTotalMs: this.assumedSessionTimeLimitTotalMs,
					}
				);
			}
		}

		// Start monitoring interval
		this.monitoringIntervalHandle = setInterval(() => {
			if (!this.isActive || this.isShowingCountdown) return;

			const remainingMs = KioInactivityTimer.GetTimeRemainingMs();
			if (this._countdownThresholdMs !== null && remainingMs <= this._countdownThresholdMs) {
				if (this.monitoringIntervalHandle !== null) {
					clearInterval(this.monitoringIntervalHandle);
					this.monitoringIntervalHandle = null;
				}
				this.startCountdown(Math.ceil(remainingMs / 1000));
			}
		}, 500);
	};

	startCountdown(initialSeconds: number) {
		if (!this.isTimerEnabled || !this.isKioAppAvailable) return;

		this.countdownSeconds = Math.max(initialSeconds);
		let lastRemainingMs = initialSeconds * 1000;

		this.countdownIntervalHandle = setInterval(() => {
			runInAction(() => {
				const currentRemainingMs = KioInactivityTimer.GetTimeRemainingMs();

				// If remaining time increased by more than 2 seconds, user must have interacted
				if (currentRemainingMs > lastRemainingMs + 2000) {
					this.cancelCountdown();
					this.startCountdownTriggerMonitoring();
					return;
				}

				lastRemainingMs = currentRemainingMs;

				if (this.countdownSeconds !== null) this.countdownSeconds--;
				if (this.countdownSeconds === 1)
					setTimeout(async () => {
						this.cancelCountdown();
						if (this._timeoutNavigationCallback) {
							this._timeoutNavigationCallback();
						} else {
							await this.startEndSessionTasks();
						}
					}, 500);
			});
		}, 1000);
	}

	cancelCountdown = () => {
		runInAction(() => {
			this.countdownSeconds = null;
			if (this.countdownIntervalHandle !== null) {
				clearInterval(this.countdownIntervalHandle);
				this.countdownIntervalHandle = null;
			}
		});

		this.startCountdownTriggerMonitoring();
	};

	startEndSessionTasks = async () => {
		await sqlite.setSetting('currentSessionId', '');

		if (!this._sessionId) {
			await sqlite.close();
			KioApp.EndSession();
			return;
		}

		if (this._isSessionEnding) {
			logger.debug.log('Session is already ending, ignoring duplicate call.');
			return;
		}

		runInAction(() => {
			this._isSessionEnding = true;
		});

		store.uiStore.setBackEnabled(false);

		this.cancelCountdown();

		store.uiStore.showLoader();

		this.beginKioWareSession();

		if (import.meta.env.DEV) await new Promise((resolve) => setTimeout(resolve, 3000));

		// Run cleanup handlers
		await logger.logGeneral.log({}, `Running ${this._cleanupHandlers.size} cleanup handlers before session end`);
		const cleanupPromises: Promise<void>[] = [];
		this._cleanupHandlers.forEach(async (handler, key) => {
			try {
				logger.debug.log(`Executing cleanup for: ${key}`);
				const result = handler();
				if (result instanceof Promise) {
					cleanupPromises.push(
						result.catch(async (error) => {
							await logger.logGeneral.error({}, `Async cleanup handler failed for ${key}:`, error);
						})
					);
				}
			} catch (error) {
				await logger.logGeneral.error({}, `Cleanup handler failed for ${key}:`, error);
			}
		});

		if (cleanupPromises.length > 0) {
			logger.debug.log(`Waiting for ${cleanupPromises.length} async cleanup handlers`);
			await Promise.race([
				Promise.allSettled(cleanupPromises),
				new Promise((resolve) => setTimeout(resolve, 60000)), // 60 second cutoff timeout
			]);
		}

		this._cleanupHandlers.clear();

		// Post session
		try {
			await logger.logGeneral.log({}, `Ending session: ${this.sessionId}`);
			await this.postKioskSession();
		} catch (error) {
			await logger.logGeneral.error({}, 'Error ending session:', error);
		}

		// Send digital receipt if requested
		if (store.accountStore.receiptType === ReceiptType.Email || store.accountStore.receiptType === ReceiptType.Text)
			try {
				await logger.logGeneral.log(
					{},
					`Digital receipt requested: ${store.accountStore.receiptType} to ${store.accountStore.digitalReceiptDestination?.email ?? store.accountStore.digitalReceiptDestination?.phone}`
				);
				await this.legacyPostGetReceipt();
				// This needs a little bit more time
				await new Promise((resolve) => setTimeout(resolve, 1000));
			} catch (error) {
				await logger.logGeneral.error({}, 'Error after digital receipt submission', error);
			}

		await sqlite.close();

		if (this.isKioAppAvailable) KioApp.EndSession();
		else window.location.reload();
	};

	get isActive() {
		return !!this.sessionId && !this._isSessionEnding;
	}

	get timeSinceLastActive(): number | null {
		return this.lastActive ? Date.now() - this.lastActive : null;
	}

	get isShowingCountdown(): boolean {
		return !this._skipCountdownModal && this.countdownSeconds !== null && this.countdownSeconds > 0;
	}

	private postKioskSession = async () => {
		const sessionData = structuredClone({
			id: this._sessionId!,
			legacyId: this._legacyId,
			kioskId: store.kioskStore.kioskId,
			startedAt: new Date(this.startedAt!),
			lastActivityAt: new Date(this.lastActive!),
			selectedLanguage: enumHelper.getSupportedLanguage(store.configStore.currentLanguage),
			selectedReceiptOptions: undefined,
			companyId: store.configStore.companyId,
			legacyLanguage: store.configStore.currentLanguageLegacyString,
		});

		await sqlite.createApiCall({
			id: ulid().toUpperCase(),
			timestamp: new Date(),
			requestData: JSON.stringify(sessionData),
			type: ApiCallServiceType.KioskSession,
		});

		await sqlite.createApiCall({
			id: ulid().toUpperCase(),
			timestamp: new Date(),
			requestData: JSON.stringify(sessionData),
			type: ApiCallServiceType.LegacyKioskSession,
		});
	};

	legacyPostNavUrl = async (pageId: string) => {
		if (!this.isActive) return;

		const navSession = {
			kioskID: store.kioskStore.kioskId,
			customerID: store.configStore.companyId,
			url: pageId,
			sessionGUID: this._legacyId!,
			timeStamp: new Date().toISOString(),
			passKey: await passKeyHelper.generatePassKey(),
			language: store.configStore.currentLanguageLegacyString,
		};

		await sqlite.createApiCall({
			id: ulid().toUpperCase(),
			timestamp: new Date(),
			requestData: JSON.stringify(navSession),
			type: ApiCallServiceType.NavSession,
		});
	};

	legacyPostGetReceipt = async () => {
		const recipient = store.accountStore.digitalReceiptDestination!.email || store.accountStore.digitalReceiptDestination!.phone!;
		if (!recipient) return null;

		await sqlite.createApiCall({
			id: ulid().toUpperCase(),
			timestamp: new Date(),
			requestData: JSON.stringify({
				dynatouchGUID: this._legacyId!,
				email: recipient,
			}),
			type: ApiCallServiceType.DigitalReceipt,
		});

		return true;
	};
}
